import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { ConnectionService } from 'ng-connection-service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { catchError, filter, interval, of, Subscription, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from './services/api.service';
import { CacheService } from './services/cache.service';
import { PerformanceMonitorService } from './services/performance-monitor.service';
import { UtilService } from './services/util.service';
import { DocumentViewerComponent } from './components/document-viewer/document-viewer.component';
import { SlidePanelComponent } from './shared/slide-panel/slide-panel.component';
import { ToasterContainerComponent } from './components/toaster-container/toaster-container.component';
import { ToasterService } from './services/toaster.service';

declare var $: any;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NgxSpinnerModule,
    DocumentViewerComponent,
    SlidePanelComponent,
    ToasterContainerComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  queryParams: any
  title = 'Gigsumo';
  status = 'Online';
  isConnected = true;
  private statusSubscription: Subscription;
  private routerSubscription: Subscription;
  Scheduler = 300000;

  // Routes that should have no body scroll (they have internal scrolling)
  private noBodyScrollRoutes = ['newjobs', 'newcandidates', 'message', 'suggestions'];
  constructor(
    private _api: ApiService,
    private activateRoute: ActivatedRoute,
    private connectionservices: ConnectionService,
    private util: UtilService,
    private route: Router,
    private swUpdate: SwUpdate,
    private dialog: MatDialog,
    private cacheService: CacheService,
    private performanceMonitor: PerformanceMonitorService,
    private toasterService: ToasterService) {
    // Initialize cache service setup that was previously in AppModule constructor
    if (
      this.cacheService.getValue("userId") != undefined &&
      this.cacheService.getValue("userId") != null &&
      this.cacheService.getValue("userId") != ""
    ) {
      localStorage.setItem("userId", this.cacheService.getValue("userId"));
    }
    if (
      this.cacheService.getValue("userName") != undefined &&
      this.cacheService.getValue("userName") != null &&
      this.cacheService.getValue("userName") != ""
    ) {
      localStorage.setItem("userName", this.cacheService.getValue("userName"));
    }

    this.connectionservices.monitor().subscribe(isConnected => {
      this.isConnected = isConnected;
      if (this.isConnected) {
        this.status = "ONLINE";
      } else {
        this.status = "OFFLINE";
        this.util.stopLoader();
      }
    });

    this.checkForUpdates();
  }


  ngOnInit(): void {
    if (environment.production) {
      // this.loadGTM(environment.gtmId);
    }
    this.setupBeforeUnloadListener();
    this.updateSleepEvent('ONLINE').subscribe(() => {
      console.log('First status updated on login');
    });
    this.startStatusCheck();
    this.updateUserOnlineOrOffline('ONLINE');
    this.checkIftheInternetrestored();
    if (environment.name == "dev") {
      // this.monitorLocalStorage();
     }

    // Listen to router events to manage body scroll
    this.setupRouteScrollManagement();

    // Auto-recover from lazy-chunk load failures (stale build after redeploy)
    this.setupChunkErrorReload();

    // Expose toaster service globally for testing
    (window as any)['gigsumoToasterService'] = this.toasterService;
    (window as any)['testToaster'] = () => {
      this.toasterService.success('✅ Success Test', 'This is a success notification!');
      this.toasterService.info('📢 Info Test', 'This is an info notification!');
      this.toasterService.warning('⚠️ Warning Test', 'This is a warning notification!');
      this.toasterService.error('❌ Error Test', 'This is an error notification!');
    };
  }

  /**
   * Recover from ChunkLoadError that occurs when a lazy route tries to fetch a
   * hashed JS chunk that no longer exists on the server (typically after a new
   * deploy while the user is on a stale build). On such a NavigationError we
   * hard-reload to the target URL once to pull fresh index.html + chunk manifest.
   * A sessionStorage guard prevents an infinite reload loop if the chunk is
   * genuinely unavailable.
   */
  private setupChunkErrorReload(): void {
    this.route.events.pipe(
      filter(event => event instanceof NavigationError || event instanceof NavigationEnd)
    ).subscribe(event => {
      // Any successful navigation clears the guard so a later chunk failure
      // (e.g. a second redeploy in the same session) can recover again.
      if (event instanceof NavigationEnd) {
        sessionStorage.removeItem('chunkReloaded');
        return;
      }

      const message = (event as NavigationError).error?.message ?? '';
      if (/ChunkLoadError|Loading chunk \d+ failed/i.test(message) &&
          !sessionStorage.getItem('chunkReloaded')) {
        sessionStorage.setItem('chunkReloaded', '1');
        const targetUrl = (event as NavigationError).url ?? '/';
        // NavigationError.url is always the path-style route ("/teamPage/members").
        // Under HashLocationStrategy (environment.useHash) we must re-add the "#";
        // otherwise the browser requests a real server path, the SPA host rewrites
        // it to index.html at the root, and the user is dumped on the home page
        // instead of being reloaded back onto the page they were navigating to.
        window.location.assign(
          environment.useHash ? `${window.location.origin}/#${targetUrl}` : targetUrl
        );
      }
    });
  }

  /**
   * Setup router event listener to disable body scroll for specific routes
   * Routes like newjobs, newcandidates, message, suggestions have internal scrolling
   */
  private setupRouteScrollManagement(): void {
    this.routerSubscription = this.route.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleBodyScroll(event.urlAfterRedirects);
    });

    // Handle initial route on app load
    const currentUrl = this.route.url;
    this.handleBodyScroll(currentUrl);
  }

  /**
   * Add or remove 'no-body-scroll-v2' class based on current route
   */
  private handleBodyScroll(url: string): void {
    const shouldDisableScroll = this.noBodyScrollRoutes.some(route => url.includes(`/${route}`));

    if (shouldDisableScroll) {
      document.body.classList.add('no-body-scroll-v2');
    } else {
      document.body.classList.remove('no-body-scroll-v2');
    }
  }
  @HostListener('window:unload', ['$event'])
  unload($event: Event): void {
    this.updateStatus('OFFLINE');

  }

  @HostListener('window:beforeunload', ['$event']) beforeunloadHandler(event) {
    this.updateStatus('OFFLINE');
  }
  checkIftheInternetrestored() {
    this.util.online$.pipe(
      filter(res => res)
    ).subscribe(d => {
      console.log("Internet restored", d);
      this.updateSleepEvent('ONLINE', false).subscribe(() => {
        console.log('First status updated on login');
      });
    });
  }
  private loadGTM(id: string): void {
    // Load the GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(script);

    // Inject the inline script once the GTM script is loaded
    script.onload = () => {
      window['dataLayer'] = window['dataLayer'] || [];
      const gtagScript = document.createElement('script');
      const inlineCode = document.createTextNode(`
       window.dataLayer = window.dataLayer || [];
       function gtag(){dataLayer.push(arguments);}
       gtag('js', new Date());
       gtag('config', '${id}');
     `);
      gtagScript.appendChild(inlineCode);
      document.head.appendChild(gtagScript);
    };
  }

  ngAfterViewInit(): void {
    window.addEventListener('beforeunload', (event) => {
      this.updateStatus('OFFLINE');
    });
  }

  ngOnDestroy() {
    // this.updateUserOnlineOrOffline('OFFLINE');
    window.removeEventListener('beforeunload', this.handleTabClose.bind(this));
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    // Clean up body class on destroy
    document.body.classList.remove('no-body-scroll-v2');
  }




  checkForUpdates() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.available.subscribe(() => {
        if (confirm("New version available. Load the new version?")) {
          window.location.reload();
        }
      });
    }
  }


  private startStatusCheck(): void {
    console.log('Scheduler --- >');
    this.statusSubscription = interval(this.Scheduler).pipe(
      tap(x => console.log('scheduled start', x)),
      switchMap(() => this.updateSleepEvent('ONLINE')),
      catchError((err) => {
        this.updateSleepEvent('OFFLINE');
        console.error('Error during status update', err);
        return [];
      })
    ).subscribe(() => {
      console.log('Status and last activity updated successfully');
    });
  }


  // Browser close methods

  private setupBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', this.handleTabClose.bind(this));
  }

  private handleTabClose(event: Event): void {
    this.updateStatus('OFFLINE');
  }



  private updateSleepEvent(status: "OFFLINE" | 'ONLINE', d = true) {
    console.log('UpdateSleep --- >', d);
    const userId = localStorage.getItem('userId');

    if (userId) {
      const url = `user/updateSleepEvent?userId=${userId}&status=${status}`;
      return this._api.create(url, {});
    } else {
      //  console.error('userId is not available in localStorage');

      return of([]);
    }
  }



  updateStatus(status: string) {
    //  console.log('UpdateStatus --- >');
    const userId = localStorage.getItem('userId');
    if (userId) {
      const url = `user/updateOnlineStatus?userId=${userId}&status=${status}`;
      this.util.startLoader();
      this._api.create(url, {}).subscribe(res => {
        this.util.stopLoader();
      }, err => {
        this.util.stopLoader();
      });
    } else {
      //  console.error('userId is not available in localStorage');
    }
  }



  private updateUserOnlineOrOffline(status: any) {
    var path = this.activateRoute.pathFromRoot[0]['_routerState']['snapshot'].url
    var b = path.substring(0, 8)
    if (b != 'undefined' && b != "" && b != '/success') {
      let url = 'user/onlineoroffline';
      let user = {
        userId: localStorage.getItem('userId'),
        online: status.toUpperCase()
      };
      this.util.startLoader()
      this._api.create(url, user).subscribe(res => {
        this.util.stopLoader()
      }, err => {
        this.util.stopLoader();

      });
    }
  }

  // this code will monitor the local storage set item and the component name where it is set;
  // this code for only debug purpose so don't remove ;
  monitorLocalStorage() {
  const originalSetItem = localStorage.setItem;

  localStorage.setItem = function (key: string, value: string) {
    console.log(` localStorage.setItem → ${key} = ${value}`);

    const stack = new Error().stack?.split('\n') || [];

    const matchLine = stack.find(line => line.includes('.component.ts'));

    if (matchLine) {
      const cleaned = matchLine.trim().split('/').pop(); // Just get file name
      console.log(` Component file: ${cleaned}`);
    } else {
      console.log(' Component info not found.');
      console.trace(); // fallback
    }

    return originalSetItem.apply(this, arguments);
  };
}
}